// Copyright ©️ 2026 eVolve MEP, LLC

import {
  type StrongOmit,
  type Label,
  type LabelTypeId,
  type CreateOrUpdateLabelRequest,
  isHexTriplet,
} from '@evolvemep/api-ts';
import { ActionIcon, Flex, useMantineTheme } from '@mantine/core';
import { useForm } from '@mantine/form';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { convertMantineColorToHex } from 'components/Mantine/ColorPickers/colorHelpers';
import { ColorAndNameFields } from 'components/Mantine/Labels/ManageLabelsModal/ColorAndNameFields';
import { isNotNil } from 'helpers/isNotNil';
import { useBlockerWhen } from 'hooks/navigation/useBlockerWhen';
import { useLabels } from 'hooks/typeCacheHooks/useLabels/useLabels';

type Props = {
  /** The label being edited, or null if creating a new label. */
  label: Label | null;
  labelTypeId: LabelTypeId;
  onLabelUpdated: (label: Label) => void;
  onCancel: () => void;
};

export const CreateOrEditLabel = ({ label, labelTypeId, onLabelUpdated, onCancel }: Props) => {
  const { updateLabel, createLabel } = useLabels(labelTypeId);

  const handleSubmit = async (values: typeof form.values) => {
    if (isNotNil(label)) {
      await updateLabel(label.labelId, values).then(onLabelUpdated);
    } else {
      await createLabel(values).then(onLabelUpdated);
    }
  };

  const theme = useMantineTheme();
  const defaultColorHex = convertMantineColorToHex('gray.1', theme.colors);
  const form = useForm<StrongOmit<CreateOrUpdateLabelRequest, 'labelTypeId'>>({
    initialValues: {
      name: label?.name ?? '',
      color: label?.color ?? defaultColorHex,
    },
    validate: {
      name: (v) => !v,
      color: (v) => !isHexTriplet(v),
    },
  });

  useBlockerWhen('create-or-edit-label', form.isDirty());

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Flex align="center" justify="space-between" gap="lg" p="sm">
        <ColorAndNameFields form={form} maxNameLength={64} />
        <Flex align="center" gap="xs">
          <ActionIcon variant="light" onClick={onCancel} disabled={form.submitting} title="Cancel">
            <EvolveIcon icon="Close" size="md" />
          </ActionIcon>
          <ActionIcon
            variant="solid"
            loading={form.submitting}
            disabled={!form.isValid() || !form.isDirty()}
            type="submit"
            title="Save"
          >
            <EvolveIcon icon="Check" size="lg" />
          </ActionIcon>
        </Flex>
      </Flex>
    </form>
  );
};
